import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useToast } from '../../context/ToastContext';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Chip,
  Stack,
  Grid,
  Divider,
  Autocomplete,
} from '@mui/material';
import { IconPlus, IconTrash, IconCheck } from '@tabler/icons-react';
import {
  createReportDefinition,
  updateReportDefinition,
  fetchReportDefinitions,
} from '../../slices/reportsSlice';

const MODULE_FIELDS = {
  students: [
    { value: 'id', label: 'المعرف' },
    { value: 'first_name', label: 'الاسم الأول' },
    { value: 'second_name', label: 'الاسم الثاني' },
    { value: 'student_id', label: 'رقم الطالب' },
    { value: 'school_class', label: 'الصف' },
    { value: 'gender', label: 'الجنس' },
    { value: 'birthdate', label: 'تاريخ الميلاد' },
    { value: 'date_of_registration', label: 'تاريخ التسجيل' },
  ],
  attendance: [
    { value: 'student_name', label: 'اسم الطالب' },
    { value: 'date', label: 'التاريخ' },
    { value: 'status', label: 'الحالة' },
    { value: 'class', label: 'الصف' },
  ],
  payments: [
    { value: 'date', label: 'التاريخ' },
    { value: 'amount', label: 'المبلغ' },
    { value: 'type', label: 'النوع' },
    { value: 'reason', label: 'السبب' },
  ],
  library: [
    { value: 'book_title', label: 'عنوان الكتاب' },
    { value: 'student_name', label: 'اسم الطالب' },
    { value: 'borrow_date', label: 'تاريخ الاستعارة' },
    { value: 'return_date', label: 'تاريخ الإرجاع' },
    { value: 'status', label: 'الحالة' },
  ],
  employees: [
    { value: 'name', label: 'الاسم' },
    { value: 'type', label: 'النوع' },
    { value: 'phone', label: 'الهاتف' },
  ],
  inventory: [
    { value: 'item_name', label: 'اسم العنصر' },
    { value: 'quantity', label: 'الكمية' },
    { value: 'unit_price', label: 'سعر الوحدة' },
    { value: 'total', label: 'الإجمالي' },
    { value: 'date', label: 'التاريخ' },
  ],
  financial: [
    { value: 'total_payments', label: 'إجمالي المدفوعات' },
    { value: 'total_recipients', label: 'إجمالي المقبوضات' },
    { value: 'net', label: 'صافي' },
  ],
};

const MODULE_OPTIONS = [
  { value: 'students', label: 'الطلاب' },
  { value: 'attendance', label: 'الحضور والغياب' },
  { value: 'payments', label: 'المدفوعات' },
  { value: 'library', label: 'المكتبة' },
  { value: 'employees', label: 'الموظفين' },
  { value: 'inventory', label: 'المستودع' },
  { value: 'financial', label: 'المالية' },
];

const CHART_TYPE_OPTIONS = [
  { value: 'table', label: 'جدول' },
  { value: 'bar', label: 'عمودي' },
  { value: 'line', label: 'خطي' },
  { value: 'pie', label: 'دائري' },
  { value: 'none', label: 'بدون رسم' },
];

const ReportBuilder = ({ reportDefinition, onSave, onCancel }) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { definitionsLoading } = useSelector((state) => state.reports);

  const [formData, setFormData] = useState({
    name: reportDefinition?.name || '',
    module: reportDefinition?.module || 'students',
    selected_fields: reportDefinition?.selected_fields || [],
    filters: reportDefinition?.filters || {},
    group_by: reportDefinition?.group_by || [],
    chart_type: reportDefinition?.chart_type || 'table',
    is_shared: reportDefinition?.is_shared || false,
  });

  const availableFields = MODULE_FIELDS[formData.module] || [];

  const handleFieldToggle = (fieldValue) => {
    setFormData((prev) => {
      const fields = [...prev.selected_fields];
      const index = fields.indexOf(fieldValue);
      if (index > -1) {
        fields.splice(index, 1);
      } else {
        fields.push(fieldValue);
      }
      return { ...prev, selected_fields: fields };
    });
  };

  const handleFilterChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      filters: { ...prev.filters, [key]: value },
    }));
  };

  const handleGroupByToggle = (field) => {
    setFormData((prev) => {
      const groupBy = [...prev.group_by];
      const index = groupBy.indexOf(field);
      if (index > -1) {
        groupBy.splice(index, 1);
      } else {
        groupBy.push(field);
      }
      return { ...prev, group_by: groupBy };
    });
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      showToast('يرجى إدخال اسم التقرير', 'error');
      return;
    }

    if (formData.selected_fields.length === 0) {
      showToast('يرجى اختيار حقل واحد على الأقل', 'error');
      return;
    }

    try {
      if (reportDefinition?.id) {
        await dispatch(
          updateReportDefinition({
            id: reportDefinition.id,
            data: formData,
          })
        ).unwrap();
        showToast('تم تحديث التقرير بنجاح', 'success');
      } else {
        await dispatch(createReportDefinition(formData)).unwrap();
        showToast('تم إنشاء التقرير بنجاح', 'success');
      }
      onSave?.();
    } catch (error) {
      showToast(error.message || 'حدث خطأ', 'error');
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        {reportDefinition ? 'تعديل تقرير' : 'إنشاء تقرير جديد'}
      </Typography>
      <Divider sx={{ my: 2 }} />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="اسم التقرير"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>الوحدة</InputLabel>
            <Select
              value={formData.module}
              label="الوحدة"
              onChange={(e) =>
                setFormData({
                  ...formData,
                  module: e.target.value,
                  selected_fields: [],
                  filters: {},
                  group_by: [],
                })
              }
            >
              {MODULE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>نوع الرسم البياني</InputLabel>
            <Select
              value={formData.chart_type}
              label="نوع الرسم البياني"
              onChange={(e) =>
                setFormData({ ...formData, chart_type: e.target.value })
              }
            >
              {CHART_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>
            الحقول المحددة
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {availableFields.map((field) => (
              <Chip
                key={field.value}
                label={field.label}
                onClick={() => handleFieldToggle(field.value)}
                color={
                  formData.selected_fields.includes(field.value)
                    ? 'primary'
                    : 'default'
                }
                variant={
                  formData.selected_fields.includes(field.value)
                    ? 'filled'
                    : 'outlined'
                }
              />
            ))}
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>
            الفلاتر
          </Typography>
          <Stack spacing={2}>
            {formData.module === 'students' && (
              <>
                <TextField
                  fullWidth
                  label="تاريخ البداية"
                  type="date"
                  value={formData.filters.date_from || ''}
                  onChange={(e) =>
                    handleFilterChange('date_from', e.target.value)
                  }
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  fullWidth
                  label="تاريخ النهاية"
                  type="date"
                  value={formData.filters.date_to || ''}
                  onChange={(e) =>
                    handleFilterChange('date_to', e.target.value)
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </>
            )}
            {(formData.module === 'attendance' ||
              formData.module === 'payments') && (
              <>
                <TextField
                  fullWidth
                  label="تاريخ البداية"
                  type="date"
                  value={formData.filters.date_from || ''}
                  onChange={(e) =>
                    handleFilterChange('date_from', e.target.value)
                  }
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  fullWidth
                  label="تاريخ النهاية"
                  type="date"
                  value={formData.filters.date_to || ''}
                  onChange={(e) =>
                    handleFilterChange('date_to', e.target.value)
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </>
            )}
          </Stack>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>
            التجميع حسب
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {availableFields.map((field) => (
              <Chip
                key={field.value}
                label={field.label}
                onClick={() => handleGroupByToggle(field.value)}
                color={
                  formData.group_by.includes(field.value) ? 'secondary' : 'default'
                }
                variant={
                  formData.group_by.includes(field.value) ? 'filled' : 'outlined'
                }
              />
            ))}
          </Box>
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.is_shared}
                onChange={(e) =>
                  setFormData({ ...formData, is_shared: e.target.checked })
                }
              />
            }
            label="مشارك مع الآخرين"
          />
        </Grid>

        <Grid item xs={12}>
          <Stack useFlexGap direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={onCancel} variant="outlined">
              إلغاء
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              startIcon={<IconCheck />}
              disabled={definitionsLoading}
            >
              حفظ
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default ReportBuilder;
